import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { useVuelidate } from "@vuelidate/core";
import { required, requiredIf } from "@vuelidate/validators";
import moment from "moment";

import { ApplicationNames, LineOfServices, ApplicationEnum, HttpStatusCodeEnum } from "../../../enums/enums";
import { ProvidersService } from "../../../services/providers-service";
import { BranchesService } from "../../../services/branches-service";
import { CentralIntakeService } from "../../../services/central-intake.service";
import { ReferralService } from "../../../services/referral-service";
import { IGetProviderItem } from "../../../resources-vue/vue-interfaces/i-providers-service";
import { ICreateInterimReferralPayload } from "../../../resources-vue/vue-interfaces/i-get-referral";
import {
    INonAdmitReasonQuery,
    INonAdmitReasonsListItem,
    IBranchResponse
} from "../../../resources-vue/vue-interfaces/i-branch";
import { IAgencyServices } from "../../../resources-vue/vue-interfaces/i-agency";
import { formatPhone, formatDateFromDate } from "../../../common/vue-helpers/modifiers/value-modifier";
import { ToastrService } from "../../../services/toastr.service";
import { PatientService } from "../../../services/patient.service";
import { AssetsService } from "../../../services/assets-service";

import PageLayout from "../../../resources-vue/vue-custom-elements/PageLayout/PageLayout.vue";
import FormSection from "../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import Pagination from "../../../resources-vue/vue-elements/Pagination/Pagination.vue";
import ErrorMessageHandler from "../../../resources-vue/vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import FormatName from "../../../resources-vue/vue-custom-elements/FormatName/FormatName.vue";
import EmptyTextHandler from "../../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";
import NewTypeahead from "../../../resources-vue/vue-custom-elements/NewTypeahead/NewTypeahead.vue";
import DatePicker from "../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import TablePrompt from "../../../resources-vue/vue-custom-elements/TablePrompt/TablePrompt.vue";

export default defineComponent({
    components: {
        NewTypeahead,
        DatePicker,
        FormatName,
        EmptyTextHandler,
        FormSection,
        PageLayout,
        Pagination,
        ErrorMessageHandler,
        TablePrompt
    },

    props: {
        resetList: {
            type: Function
        },
        patient: { type: Object },
        agencyServices: { type: Array<IAgencyServices> }
    },

    validations() {
        return {
            referTo: { required },
            selectedBranch: { required: requiredIf(this.referTo !== null) },
            nonAdmitDate: { required: requiredIf(this.isNonAdmit === true) },
            selectedNonAdmitReason: { required: requiredIf(this.isNonAdmit == true) }
        };
    },

    data() {
        return {
            _toastrService: null,
            showTransferForm: false,
            isNonAdmit: false,
            nonAdmitDate: "",
            nonAdmitReason: "",
            nonAdmitReasonId: null,
            selectedNonAdmitReason: "",
            ApplicationNamesEnum: ApplicationNames,
            lineOfServiceEnumMap: LineOfServices,
            ApplicationEnum,
            providerList: [],
            allProviderList: [],
            allBranchList: [],
            nonAdmitReasons: [],
            referTo: null as IAgencyServices,
            referableAgencyServices: null,
            selectedBranch: null,
            locationId: null,
            accountId: null,
            _providersService: null,
            _branchesService: null,
            _centralIntakeService: null,
            _referralService: null,
            _patientService: null,
            _assetService: null,
            v$: useVuelidate(),
            isDeleteRequested: false as boolean,
            isDeleteInProgress: false as boolean,
            canViewDoc: true as boolean,
            defaultAssetId: "00000000-0000-0000-0000-000000000000",
            listOfStatuses: [
                "Active",
                "Discharge",
                "Discharged",
                "Pending",
                "Non-Admitted",
                "NonAdmit",
                "Non-Admit",
                "Hospitalized",
                "NonAdmission",
                "None",
                "Non-Admit",
                "Hospitalized",
                null
            ],
            includePatientCharts: false as boolean,
            formatPhone,
            formatDateFromDate
        };
    },

    watch: {
        selectedBranch(newValue: any) {
            if (!newValue) {
                this.locationId = "";
                return;
            }
            this.locationId = this.selectedBranch.id;
            this.accountId = this.selectedBranch.accountId;
        },
        selectedNonAdmitReason(newValue: any) {
            if (newValue) {
                this.nonAdmitReasonId = newValue.value;
                this.nonAdmitReason = newValue.name;
            } else {
                this.nonAdmitReasonId = null;
                this.nonAdmitReason = "";
            }
        }
    },
    async created() {
        this._toastrService = Container.instance.get(ToastrService);
        this._providersService = Container.instance.get(ProvidersService);
        this._branchesService = Container.instance.get(BranchesService);
        this._centralIntakeService = Container.instance.get(CentralIntakeService);
        this._referralService = Container.instance.get(ReferralService);
        this._patientService = Container.instance.get(PatientService);
        this._assetService = Container.instance.get(AssetsService);
        const providers = await this._providersService.getProvider();
        this.allBranchList = await this._branchesService.getAllBranches();
        this.allProviderList = providers.filter((provider: any) => provider.branches.length > 0);
        this.referableAgencyServices = this.agencyServices.filter((agency) => agency.id !== this.patient.application);
        if (this.patient.status === "NonAdmission") {
            this.patient.status = "Non-Admitted";
        }
    },
    methods: {
        toggleShowTransferForm() {
            this.showTransferForm = !this.showTransferForm;
            this.getNonAdmitReasons();
        },
        updateBranches(application: number) {
            this.providerList = [];
            this.selectedBranch = null;

            if ([ApplicationEnum.AxxessHospiceFE, ApplicationEnum.AxxessPalliative].includes(application)) {
                this.providerList = this.allProviderList.filter(
                    (provider) => provider.branches[0].application == application
                );
            } else {
                let providers: { [key: string]: string } = {};
                this.allBranchList.forEach((item) => {
                    if (item.providerId && item.application == application) {
                        providers[item.providerId] = item.providerName;
                    }
                });
                for (let providerItem of Object.keys(providers)) {
                    let obj: IGetProviderItem = { id: null, name: null, branches: null };
                    let branch: IBranchResponse[] = [];
                    this.allBranchList.forEach((item) => {
                        if (providerItem == item.providerId) branch.push(item);
                    });
                    obj.id = providerItem;
                    obj.branches = branch;
                    obj.name = providers[providerItem];
                    this.providerList.push(obj);
                }
            }
        },
        async getNonAdmitReasons() {
            let providerIds: string[] = [];
            this.allBranchList.forEach((branch: any) => {
                if (branch.accountId == this.patient.accountId && !providerIds?.includes(branch.providerId)) {
                    providerIds.push(branch.providerId);
                }
            });
            const objQuery: INonAdmitReasonQuery = {
                accountId: this.patient.accountId,
                providerIds: providerIds,
                application: this.patient.application
            };
            const nonAdmitReasonsList = await this._centralIntakeService.getNonAdmitReasons(objQuery);
            if (nonAdmitReasonsList.data) {
                this.nonAdmitReasons = nonAdmitReasonsList.data?.map((reason: INonAdmitReasonsListItem) => ({
                    value: reason.id,
                    name: reason.reason,
                    commentRequired: reason.isCommentRequired || false
                }));
            }
        },

        closeNonAdmitForm() {
            this.showTransferForm = !this.showTransferForm;
        },

        async convertToRefer() {
            const validated = await this.v$.$validate();

            if (validated) {
                if (this.patient.application === ApplicationEnum.AxxessPalliative && this.patient.isPatient) {
                    this.patient.id = this.patient.patientId;
                }

                if (
                    this.referTo.id == ApplicationEnum.AxxessHospiceFE ||
                    this.referTo.id == ApplicationEnum.AxxessPalliative
                ) {
                    let lineOfService: number;
                    if (this.referTo.id == ApplicationEnum.AxxessHospiceFE) {
                        lineOfService = this.lineOfServiceEnumMap.enum.Hospice;
                    } else {
                        lineOfService = this.lineOfServiceEnumMap.enum.PalliativeCare;
                    }
                    const payload: ICreateInterimReferralPayload = {
                        accountId: this.accountId,
                        agencyId: this.accountId,
                        application: this.referTo.id,
                        firstName: this.patient.firstName,
                        lastName: this.patient.lastName,
                        lineOfService: lineOfService,
                        locationId: this.locationId
                    };
                    const response = await this._referralService.createInterimReferral(payload);
                    let referralId = response.referralId;

                    this.$router.push(
                        `/axxess-central/create/referral?id=${referralId}&externalReferralId=${this.patient.id}&accountId=${this.accountId}&application=${this.referTo.id}&locationId=${this.locationId}&isCreate=true`
                    );
                } else if (this.referTo.id == ApplicationEnum.AgencyCore) {
                    this.$router.push(
                        `/axxess-central/create/homehealth-referral?id=0000000-0000-0000-0000-0000000000000&externalReferralId=${this.patient.id}&accountId=${this.accountId}&application=${this.referTo.id}&locationId=${this.locationId}&isCreate=true`
                    );
                } else if (this.referTo.id == ApplicationEnum.HomeCare) {
                    this.$router.push({
                        path: "/axxess-central/create/homecare-referral",
                        query: {
                            account: this.accountId,
                            externalReferralId: this.patient.id,
                            isNonMedical: String(this.referTo.isNonMedical),
                            isCreate: "true",
                            lineOfService: this.patient.serviceLine
                        }
                    });
                } else {
                    this.$router.push(
                        `/axxess-central/create/homehealth-referral/0000000-0000-0000-0000-0000000000000/account/${this.accountId}/application/${this.referTo.id}/?isCreate=true&includePatientCharts=${this.includePatientCharts}`
                    );
                }
            } else {
                this._toastrService.error({
                    title: `Validation Error`,
                    message: "Please fill required fields"
                });
            }
        },

        updateNonAdmitDate(date: string) {
            this.nonAdmitDate = date;
        },

        isNonAdmitDisabled() {
            return (
                this.patient.status === "Discharged" ||
                this.patient.status === "Discharge" ||
                this.patient.status === "Active" ||
                this.patient.status === "NonAdmission" ||
                this.patient.status === "NonAdmit" ||
                this.patient.status === "Non-Admitted" ||
                this.patient.status === "Non-Admit" ||
                this.patient.status === "Hospitalized"
            );
        },

        confirmDeleteReferral() {
            this.isDeleteRequested = true;
        },

        async deleteReferral() {
            try {
                this.isDeleteInProgress = true;
                const payload = {
                    Id: this.patient.id
                };
                const response = await this._referralService.deleteExternalReferralById(payload);
                if (
                    response.statusCode == HttpStatusCodeEnum.NoContent ||
                    response.statusCode == HttpStatusCodeEnum.Ok
                ) {
                    this._toastrService.success({
                        message: `Referral has been successfully deleted.`,
                        title: `Referral Deleted`
                    });
                    this.resetList();
                } else if (response.statusCode == HttpStatusCodeEnum.Forbidden) {
                    this._toastrService.error({
                        title: `Error - Delete Referral`,
                        message: `Referral is Already Deleted`
                    });
                }
            } catch (error) {
                console.log(error);
                this._toastrService.error({
                    title: `Error - Delete Referral`,
                    message: `There was a problem while deleting the Referral. Please try again.`
                });
            } finally {
                this.isDeleteInProgress = false;
                this.isDeleteRequested = false;
            }
        },

        getApplicableService(ServiceLine: number) {
            return this.ApplicationNamesEnum.getDescriptionUsingValue(ServiceLine);
        },

        cancelDeleteReferral() {
            this.isDeleteRequested = false;
        },

        async downloadExternalRefrral() {
            const response = await this._assetService.getExternalReferralDocument({
                assetId: this.patient.assetId
            });
            const url = response.storageUrl + "/api/v1/assets?token=" + response.token;
            window.open(url, "_blank");
        }
    }
});
